import { ICON__file_output } from '@/assets/icons_lucide';
import { UI_PRIMITIVE__button_with_icon_animated } from '@/components/ui/UI_PRIMITIVE__button_with_icon_animated';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import { HOOK_GRID2D_CELL__button_size } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL__button_size';
import { REMOTE__generation_update_visibility } from '@/state/generation/status/REMOTE__generation_update_visibility';
import { FN_GENERATION__consume_content } from '@/utils/UTILS__generation';
import { set_current_generation } from '@/utils/utils_client_workspace_child_entity_viewers';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { ENUM__generation_status } from '@shared/dto/generation/_enums/ENUM__generation_status';
import { DTO__generation } from '@shared/dto/generation/DTO__generation';
import { DTO__grid2d_cell } from '@shared/dto/grid2d_cell/DTO__grid2d_cell';
import { ENUM__grid2d_cell_size } from '@shared/dto/grid2d_cell/ENUM__grid2d_cell_size';
import { useMemo, useState } from 'react';

interface Props {
  cell: DTO__grid2d_cell;
  wrapper_class_name: string;
}

export function BUTTON_GRID2D_IMAGE__consume_content({ cell, wrapper_class_name }: Props) {
  const dispatch = use_dispatch();
  const { toast } = useToast();

  const { generation_current, generations_map } = use_slice_generation(['generation_current', 'generations_map']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user', 'workspace_current']);

  const grid2d_cell_button_size = HOOK_GRID2D_CELL__button_size(user_settings.ui_grid2d_cell_size);

  const generation_consumer = useMemo(() => get_generation_consumer(), [generation_current, generations_map]);

  const [is_loading, set_is_loading] = useState(false);

  /*
   * EVENTS
   */

  async function onClick(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();

    if (is_loading) return;

    set_is_loading(true);

    let is_consumed = await FN_GENERATION__consume_content(generation_current, cell.hash_sha256_content, toast);

    /*
     *
     */

    let generation = generation_current.clone({ is_visible: false });

    await dispatch(REMOTE__generation_update_visibility(generation));

    /*
     *
     */
    if (is_consumed) {
      await set_current_generation(generation_current.graph_field_consumer.meta.graph_id);
    }

    set_is_loading(false);
  }

  /*
   * METHODS
   */

  function get_generation_consumer() {
    let out = DTO__generation.CREATE_STUB();

    if (generation_current.is_stub()) return out;
    if (generation_current.graph_field_consumer.is_stub()) return out;
    if (!generations_map.has(generation_current.graph_field_consumer.meta.graph_id)) return out;

    out = generations_map.get_or_assert(generation_current.graph_field_consumer.meta.graph_id);

    return out;
  }

  function is_generation_consumer_in_progress_or_locked() {
    if (generation_consumer.is_stub()) return false;

    return generation_consumer.status == ENUM__generation_status.IN_PROGRESS || generation_consumer.status == ENUM__generation_status.LOCKED;
  }

  function is_disabled() {
    return is_loading || is_generation_consumer_in_progress_or_locked();
  }

  /*
   * RENDERS
   */

  if (generation_current.is_stub()) return null;
  if (!generation_current.image_can_be_consumed(cell.image)) return null;
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return null;

  return (
    <div className={wrapper_class_name}>
      <UI_PRIMITIVE__button_with_icon_animated
        Icon={ICON__file_output}
        disabled={is_disabled()}
        iconClassNameBase={`!text-green-500 !size-5 !duration-300
             ${user_settings.ui_grid2d_cell_size === ENUM__grid2d_cell_size.L ? '!size-7' : ''}`}
        iconClassNameHovered="!text-green-500"
        variant="no-border-on-component"
        onClick={onClick}
        className={`p-0 rounded-lg  bg-bar cursor-pointer}
          ${grid2d_cell_button_size}`}
      />
    </div>
  );
}
